import React from "react";
import { HeaderProps } from "./types";
import { useLoyaltySession } from "../../hooks/useLoyaltySession";
import LoyaltyMemberChip from "./LoyaltyMemberChip";

const Header: React.FC<HeaderProps> = ({
    isMenuOpen,
    setIsMenuOpen,
}) => {
    const { sessionActive, link, traveler } = useLoyaltySession();
    const firstName = traveler?.profile?.first_name ?? "Movmo traveler";

    return (
        <header className="absolute top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/70 to-transparent">
            <nav className="mx-auto max-w-[1192px] px-5">
                <div className="flex items-center justify-between py-4">
                    {/* Logo */}
                    <div className="flex items-center">
                        <img
                            src="/PR.svg"
                            alt="Philippine Airlines"
                            className="h-9 lg:h-10 w-auto"
                            onError={(e) => {
                                e.currentTarget.style.display = "none";
                                e.currentTarget.nextElementSibling?.classList.remove(
                                    "hidden"
                                );
                            }}
                        />
                        <div className="hidden text-white font-semibold text-lg">
                            Philippine Airlines
                        </div>
                    </div>

                    {/* Desktop Navigation */}
                    {/* space-x-6 (not -8): with the Movmo chip's extra
                        ~100px, -8's 32px gaps left "Log in / Sign up"
                        wrapping onto two lines at 1280px even though the
                        chip itself no longer collides with it. */}
                    <div className="hidden lg:flex items-center space-x-6">
                        <button className="text-white hover:text-gray-200 transition-colors text-[17px] font-normal">
                            Book
                        </button>
                        <button className="text-white hover:text-gray-200 transition-colors text-[17px] font-normal">
                            Explore
                        </button>
                        <button className="text-white hover:text-gray-200 transition-colors text-[17px] font-normal">
                            Manage
                        </button>
                        <button className="text-white hover:text-gray-200 transition-colors text-[17px] font-normal">
                            Prepare
                        </button>
                        <button className="text-white hover:text-gray-200 transition-colors text-[17px] font-normal">
                            Mabuhay Miles
                        </button>
                        <button className="text-white hover:text-gray-200 transition-colors text-[17px] font-normal">
                            Help
                        </button>

                        {/* Search Icon */}
                        <button className="text-white hover:text-gray-200 transition-colors">
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                            </svg>
                        </button>

                        {/* Language Selector */}
                        <button
                            className="flex items-center text-white px-2 py-1.5 rounded transition-colors text-[17px] font-normal"
                            style={{ backgroundColor: "#0047ba" }}
                            onMouseEnter={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                    "#003a96")
                            }
                            onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                    "#0047ba")
                            }
                        >
                            <svg
                                className="w-5 h-5 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                                />
                            </svg>
                            <svg
                                className="w-3 h-3 ml-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        </button>

                        {/* Movmo loyalty single-login states (MOVMO-541,
                            layered on top of the Movmo identity chip): anonymous -> unchanged "Log in / Sign up";
                            linked-without-loyalty -> name + a "Link Mabuhay
                            Miles" CTA that asks the landing page to open the
                            link ceremony (kept out of Header to avoid modal
                            state here); linked-with-loyalty -> the member chip
                            with tier + miles. MOVMO-567: sessionActive/link
                            are wire-authoritative (the GET /me response's
                            loyalty group) — see useLoyaltySession.ts. The
                            link ceremony behind the CTA is transition
                            theater only and does not by itself flip this
                            state. */}
                        {!traveler && (
                            <button className="text-white hover:text-gray-200 transition-colors text-[17px] font-normal">
                                Log in / Sign up
                            </button>
                        )}
                        {traveler && !sessionActive && (
                            <div className="flex items-center gap-2">
                                <span className="text-[15px] text-white whitespace-nowrap">
                                    {firstName}
                                </span>
                                <button
                                    type="button"
                                    onClick={() =>
                                        window.dispatchEvent(
                                            new CustomEvent(
                                                "movmo:open-loyalty-link",
                                            ),
                                        )
                                    }
                                    className="rounded-full border border-white/70 bg-white/10 px-3 py-1.5 text-[15px] text-white whitespace-nowrap transition-colors hover:bg-white/20"
                                >
                                    Link Mabuhay Miles
                                </button>
                            </div>
                        )}
                        {sessionActive && link && (
                            <LoyaltyMemberChip
                                firstName={firstName}
                                link={link}
                            />
                        )}
                    </div>

                    {/* Mobile Navigation Icons */}
                    <div className="lg:hidden flex items-center space-x-4">
                        {/* Search Icon */}
                        <button className="text-white">
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                            </svg>
                        </button>

                        {/* Language/Globe Icon */}
                        <button className="text-white">
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                                />
                            </svg>
                        </button>

                        {/* User Profile Icon */}
                        <button className="text-white">
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                            </svg>
                        </button>

                        {/* Mobile menu button */}
                        <button
                            className="text-white"
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                        >
                            <svg
                                className="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d={
                                        isMenuOpen
                                            ? "M6 18L18 6M6 6l12 12"
                                            : "M4 6h16M4 12h16M4 18h16"
                                    }
                                />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Mobile Navigation */}
                {isMenuOpen && (
                    <div className="lg:hidden bg-black/90 backdrop-blur-md rounded-b-lg">
                        <div className="px-4 py-3 space-y-3">
                            <button className="block w-full text-left text-white hover:text-gray-200 py-2">
                                Book
                            </button>
                            <button className="block w-full text-left text-white hover:text-gray-200 py-2">
                                Explore
                            </button>
                            <button className="block w-full text-left text-white hover:text-gray-200 py-2">
                                Manage
                            </button>
                            <button className="block w-full text-left text-white hover:text-gray-200 py-2">
                                Prepare
                            </button>
                            <button className="block w-full text-left text-white hover:text-gray-200 py-2">
                                Mabuhay Miles
                            </button>
                            <button className="block w-full text-left text-white hover:text-gray-200 py-2">
                                Help
                            </button>
                        </div>
                    </div>
                )}
            </nav>
        </header>
    );
};

export default Header;
